import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { nhost } from "@/lib/nhost";
import { getPostLoginPath, takeGoogleSignInState, NEW_GOOGLE_USER_KEY } from "@/lib/authRedirect";

/**
 * Google "welcome back" handler.
 *
 * After Google login, Nhost sends people back to /login?code=...
 * This finishes the login safely (PKCE), makes sure they have a profile
 * card, then sends them to the right page.
 * Admin pages are never touched.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (location.pathname.startsWith("/admin")) return;
    if (location.pathname.startsWith("/forgot-password")) return;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const oauthError = params.get("error");
    if (!code && !oauthError) return;

    handledRef.current = true;

    // Hide the code from the address bar
    window.history.replaceState({}, "", location.pathname);

    const { verifier, redirect } = takeGoogleSignInState();

    if (oauthError || !code || !verifier) {
      navigate("/login?google_error=1", { replace: true });
      return;
    }

    (async () => {
      try {
        await nhost.auth.tokenExchange({ code, codeVerifier: verifier });

        localStorage.setItem("mad_keep_signed_in", "true");
        localStorage.setItem("mad_last_active", String(Date.now()));

        let isReseller = false;
        try {
          const res = await nhost.functions.post("/ensure-profile", {});
          const body = res.body as { isReseller?: boolean; isNewUser?: boolean };
          isReseller = !!body?.isReseller;
          if (body?.isNewUser) sessionStorage.setItem(NEW_GOOGLE_USER_KEY, "true");
        } catch {
          /* still let them in */
        }

        navigate(getPostLoginPath({ isReseller, redirect }), { replace: true });
      } catch {
        navigate("/login?google_error=1", { replace: true });
      }
    })();
  }, [navigate, location.pathname, location.search]);

  return null;
};

export default AuthCallback;